import { protectedApi } from "../common/protectedApi";
import { APIS } from "../constants/apiConstant";
import { normalizeBoardAttachment } from "./projectBoardAttachment";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const cacheBust = () => ({ _t: Date.now() });

export const isValidBoardId = (boardId) =>
  Boolean(boardId && boardId !== "undefined" && UUID_REGEX.test(String(boardId)));

const normalizeBoardSummary = (board = {}) => ({
  ...board,
  boardId: board.boardId || board.board_id || board.id,
});

const normalizeList = (list = {}) => ({
  ...list,
  listId: list.listId || list.list_id,
  boardId: list.boardId || list.board_id,
});

const normalizeCard = (card = {}) => ({
  ...card,
  cardId: card.cardId || card.card_id,
  listId: card.listId || card.list_id,
  boardId: card.boardId || card.board_id,
  ticketNo: card.ticketNo || card.ticket_no || "",
});

const normalizeBoardPayload = (payload = null) => {
  if (!payload) return null;
  return {
    ...payload,
    board: payload.board ? normalizeBoardSummary(payload.board) : null,
    lists: (payload.lists || []).map(normalizeList),
    cards: (payload.cards || []).map(normalizeCard),
    members: payload.members || [],
    activity: payload.activity || [],
    commentsByCard: payload.commentsByCard || {},
  };
};

export const fetchAllBoards = async (search = "") => {
  const response = await protectedApi({
    url: APIS.PROJECT_BOARD_ALL,
    method: "GET",
    params: cacheBust(),
  });
  const rawBoards = response?.data?.data?.boards ?? response?.data?.boards ?? [];
  let boards = (Array.isArray(rawBoards) ? rawBoards : []).map(normalizeBoardSummary);
  const q = String(search || "").trim().toLowerCase();
  if (q) {
    boards = boards.filter(
      (b) =>
        b.name?.toLowerCase().includes(q) ||
        b.projectName?.toLowerCase().includes(q) ||
        b.description?.toLowerCase().includes(q)
    );
  }
  return boards.filter((b) => b.boardId);
};

export const fetchBoardById = async (boardId) => {
  if (!isValidBoardId(boardId)) {
    throw new Error("Invalid board id.");
  }
  const url = APIS.PROJECT_BOARD_BY_ID.replace(":boardId", boardId);
  const response = await protectedApi({
    url,
    method: "GET",
    params: cacheBust(),
  });
  return normalizeBoardPayload(response?.data?.data || null);
};

export const createBoardApi = async (payload) => {
  const response = await protectedApi({
    url: APIS.PROJECT_BOARD_CREATE,
    method: "POST",
    data: payload,
  });
  const data = response?.data?.data || {};
  return {
    ...data,
    boardId: data.boardId || data.board_id,
  };
};

export const updateBoardApi = async (boardId, payload) => {
  const url = APIS.PROJECT_BOARD_UPDATE.replace(":boardId", boardId);
  const response = await protectedApi({
    url,
    method: "PATCH",
    data: payload,
  });
  return response?.data?.data;
};

export const deleteBoardApi = async (boardId) => {
  const url = APIS.PROJECT_BOARD_DELETE.replace(":boardId", boardId);
  const response = await protectedApi({ url, method: "DELETE" });
  return response?.data?.data;
};

export const fetchCardComments = async (cardId) => {
  const url = APIS.PROJECT_BOARD_CARD_COMMENTS.replace(":cardId", cardId);
  const response = await protectedApi({
    url,
    method: "GET",
    params: cacheBust(),
  });
  return response?.data?.data?.comments || [];
};

export const addCardCommentApi = async (cardId, body) => {
  const url = APIS.PROJECT_BOARD_CARD_COMMENTS.replace(":cardId", cardId);
  const response = await protectedApi({
    url,
    method: "POST",
    data: { body },
  });
  return response?.data?.data;
};

export const fetchCardAttachments = async (cardId) => {
  const url = APIS.PROJECT_BOARD_CARD_ATTACHMENTS.replace(":cardId", cardId);
  const response = await protectedApi({
    url,
    method: "GET",
    params: cacheBust(),
  });
  const items = response?.data?.data?.attachments || [];
  return items.map(normalizeBoardAttachment);
};

export const uploadCardAttachmentApi = async (cardId, file) => {
  const url = APIS.PROJECT_BOARD_CARD_ATTACHMENTS.replace(":cardId", cardId);
  const formData = new FormData();
  formData.append("file", file);
  const response = await protectedApi({
    url,
    method: "POST",
    data: formData,
  });
  const data = response?.data?.data;
  return data ? normalizeBoardAttachment(data) : null;
};

export const createCardApi = async (listId, payload) => {
  const url = APIS.PROJECT_BOARD_CARDS.replace(":listId", listId);
  const response = await protectedApi({
    url,
    method: "POST",
    data: payload,
  });
  return response?.data?.data;
};

export const createListApi = async (boardId, payload) => {
  if (!isValidBoardId(boardId)) {
    throw new Error("Invalid board id.");
  }
  const url = APIS.PROJECT_BOARD_LISTS.replace(":boardId", boardId);
  const response = await protectedApi({
    url,
    method: "POST",
    data: payload,
  });
  return response?.data?.data;
};

export const updateListApi = async (listId, payload) => {
  const url = APIS.PROJECT_BOARD_LIST_UPDATE.replace(":listId", listId);
  const response = await protectedApi({
    url,
    method: "PATCH",
    data: payload,
  });
  return response?.data?.data;
};

export const updateCardApi = async (cardId, payload) => {
  const url = APIS.PROJECT_BOARD_CARD.replace(":cardId", cardId);
  const response = await protectedApi({
    url,
    method: "PATCH",
    data: payload,
  });
  return response?.data?.data;
};

export const deleteCardApi = async (cardId) => {
  const url = APIS.PROJECT_BOARD_CARD_DELETE.replace(":cardId", cardId);
  const response = await protectedApi({ url, method: "DELETE" });
  return response?.data?.data;
};

export const moveCardApi = async (cardId, payload) => {
  const url = APIS.PROJECT_BOARD_CARD_MOVE.replace(":cardId", cardId);
  const response = await protectedApi({
    url,
    method: "PATCH",
    data: payload,
  });
  return response?.data?.data;
};

export const moveBoardListsApi = async (boardId, payload) => {
  if (!isValidBoardId(boardId)) {
    throw new Error("Invalid board id.");
  }
  const url = APIS.PROJECT_BOARD_LISTS_MOVE.replace(":boardId", boardId);
  const response = await protectedApi({
    url,
    method: "PATCH",
    data: payload,
  });
  return response?.data?.data;
};

export const searchBoardCardsApi = async (query = "") => {
  const response = await protectedApi({
    url: APIS.PROJECT_BOARD_SEARCH,
    method: "GET",
    params: { q: query, ...cacheBust() },
  });
  return response?.data?.data?.results || [];
};
